import { Injectable } from '@angular/core';
import { PlannedError } from '@platform-frontend/util';
import { ADYEN_PROFILE, createEncryption, CSEInstance, CSEPayload } from '../adyen';
import { CreditCardDetails } from './credit-card-details';
import { CreditCardEssentials } from './credit-card-essentials';
import { CreditCardExpiry } from './credit-card-expiry';
import { CreditCardServiceOptions } from './credit-card-service-options';
import { CreditCardType } from './credit-card-type';

@Injectable({
  providedIn: 'root'
})
export class CreditCardService {
  private adyenCse: CSEInstance | undefined;

  /**
   * Expands essential credit card information into a plethora of additional properties
   * including an encrypted string for Adyen. Also simplifies the existing properties
   * by removing whitespaces.
   *
   * @param creditCard Minimal information to specify the credit card
   */
  getDetails(creditCard: CreditCardEssentials): CreditCardDetails {
    const holderName = creditCard.holderName.trim();
    const number = this.removeWhitespace(creditCard.number);
    const cvc = this.removeWhitespace(creditCard.cvc);
    const expiry = this.parseExpiry(creditCard.expiry, { removeWhitespace: true, throwOnMismatch: true });

    return {
      cardType: this.getCardType(number, { throwOnMismatch: true }),
      holderName,
      number,
      maskedNumber: number.slice(-4),
      cvc,
      expiry: this.formatExpiry(expiry),
      expiryMonth: expiry.month,
      expiryYear: expiry.year,
      encryptedString: this.encryptWithAdyenCse({
        holderName,
        number,
        cvc,
        expiryMonth: this.formatAsTwoDigit(expiry.month),
        expiryYear: `${expiry.year}`,
        generationtime: new Date().toISOString()
      })
    };
  }

  private encryptWithAdyenCse(payload: CSEPayload): string {
    const encryptedString = this.getAdyenCse().encrypt(payload);

    if (encryptedString) {
      return encryptedString;
    } else {
      throw new PlannedError('ENCRYPTION_FAILED');
    }
  }

  /**
   * Determines the card type for a given credit card number.
   *
   * @param number The credit card number / primary account number
   * @param options Optional flags to customize method behavior, all disabled by default
   * @see https://stackoverflow.com/questions/72768/how-do-you-detect-credit-card-type-based-on-number
   */
  getCardType(number: string | null | undefined, options?: CreditCardServiceOptions & { throwOnMismatch: true }): CreditCardType;
  getCardType(number: string | null | undefined, options?: CreditCardServiceOptions): CreditCardType | null;
  getCardType(number: string | null | undefined, options: CreditCardServiceOptions = {}): CreditCardType | null {
    if (options.removeWhitespace) {
      number = this.removeWhitespace(number);
    } else {
      number = number || '';
    }

    if (number.match(/^4[0-9]{6,}$/)) {
      return 'Visa';
    } else if (number.match(/^(5[1-5][0-9]{5,}|222[1-9][0-9]{3,}|22[3-9][0-9]{4,}|2[3-6][0-9]{5,}|27[01][0-9]{4,}|2720[0-9]{3,})$/)) {
      return 'MasterCard';
    } else if (number.match(/^(3[47][0-9]{5,})$/)) {
      return 'AmericanExpress';
    } else if (number.match(/^(6(?:011|5[0-9]{2})[0-9]{3,})$/)) {
      return 'Discover';
    } else if (number.match(/^((?:2131|1800|35[0-9]{3})[0-9]{3,})$/)) {
      return 'JCB';
    } else if (options.throwOnMismatch) {
      throw new Error('Unknown credit card type');
    } else {
      return null;
    }
  }

  /**
   * Validates a given credit card number.
   *
   * @param number The credit card number / primary account number
   */
  validateNumber(number: string): boolean {
    return this.getAdyenCse().validate({ number: number }).valid;
  }

  private getAdyenCse(): CSEInstance {
    if (!this.adyenCse) {
      this.adyenCse = createEncryption(ADYEN_PROFILE.key, {});
    }
    return this.adyenCse;
  }

  /**
   * Validates a given expiry string for the right format 'mm/yy'
   *
   * @param expiry The expiry string to be validated
   */
  validateExpiryFormat(expiry: string): boolean {
    const parsedExpiry = this.parseExpiry(expiry, { removeWhitespace: true });
    return parsedExpiry !== null;
  }

  /**
   * Validates a given expiry string in the format 'mm/yy' for validity at a reference time.
   *
   * @param expiry The expiry string to be validated
   * @param reference The reference time to validate against
   */
  validateExpiryDate(expiry: string, reference: Date = new Date()): boolean {
    const parsedExpiry = this.parseExpiry(expiry, { removeWhitespace: true });

    if (parsedExpiry === null) {
      return false;
    }

    const expMonth = parsedExpiry.month;
    const expYear = parsedExpiry.year;
    const refMonth = reference.getMonth() + 1;
    const refYear = reference.getFullYear();

    return expYear > refYear || (expYear === refYear && expMonth >= refMonth);
  }

  /**
   * Parses a given expiry string in the format 'mm/yy' for access to month and year properties.
   *
   * @param number The expiry string to be parsed
   * @param options Optional flags to customize method behavior, all disabled by default
   */
  parseExpiry(expiry: string | null | undefined, options?: CreditCardServiceOptions & { throwOnMismatch: true }): CreditCardExpiry;
  parseExpiry(expiry: string | null | undefined, options?: CreditCardServiceOptions): CreditCardExpiry | null;
  parseExpiry(expiry: string | null | undefined, options: CreditCardServiceOptions = {}): CreditCardExpiry | null {
    if (options.removeWhitespace) {
      expiry = this.removeWhitespace(expiry);
    } else {
      expiry = expiry || '';
    }

    const matcherResult = expiry.match(/^([0-9]{2})\/([0-9]{2})$/);

    if (matcherResult && matcherResult[1] >= '01' && matcherResult[1] <= '12') {
      return {
        month: parseInt(matcherResult[1], 10),
        year: 2000 + parseInt(matcherResult[2], 10)
      };
    } else if (options.throwOnMismatch) {
      throw new Error('Invalid expiry format');
    } else {
      return null;
    }
  }

  /**
   * Format a credit card number to be displayed in groups of four digits
   *
   * @param number The credit card number to be formatted
   */
  formatNumber(number: string | null | undefined): string {
    const plainNumber = this.removeWhitespace(number);
    const groups = plainNumber.match(/.{1,4}/g) || [plainNumber];
    return groups.join(' ');
  }

  /**
   * Formats expiry details in the format 'mm/yy'.
   *
   * @param expiry The expiry object to be formatted
   */
  formatExpiry(expiry: CreditCardExpiry): string {
    return `${this.formatAsTwoDigit(expiry.month)}/${this.formatAsTwoDigit(expiry.year % 100)}`;
  }

  private formatAsTwoDigit(number: number): string {
    return `0${number}`.slice(-2);
  }

  private removeWhitespace(value: string | null | undefined): string {
    if (value) {
      return value.replace(/\s/g, '');
    } else {
      return '';
    }
  }
}
